import React, { useState, useEffect } from 'react';
import { Mail, Plus, X, Save, Loader2, UserCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getBackendUrl } from '../utils/getBackendUrl';
import { toast } from 'sonner';

const API = getBackendUrl() + '/api';

export const NotificationEmailSettings = ({ user }) => {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchNotificationEmails();
  }, []);

  const fetchNotificationEmails = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settings/notification-emails`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch notification emails');
      }
      
      const data = await response.json();
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Error fetching notification emails:', error);
      toast.error('Failed to load notification email settings');
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmail = () => {
    const trimmedEmail = newEmail.trim();
    
    if (!trimmedEmail) {
      toast.error('Please enter an email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (emails.some(email => email.toLowerCase() === trimmedEmail.toLowerCase())) {
      toast.error('This email is already in the list');
      return;
    }

    setEmails(prev => [...prev, trimmedEmail]);
    setNewEmail('');
  };

  const removeEmail = (emailToRemove) => {
    setEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  const saveEmailSettings = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/settings/notification-emails`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emails })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save notification emails');
      }

      toast.success('Notification email settings saved successfully');
    } catch (error) {
      console.error('Error saving notification emails:', error);
      toast.error(error.message || 'Failed to save notification email settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <UserCheck className="w-6 h-6 text-green-400" />
        <h3 className="text-xl font-bold text-white">Registration Notification Emails</h3>
      </div>

      <div className="space-y-4">
        <p className="text-slate-300 text-sm">
          Add email addresses that should receive notifications when new users register and need approval.
          These emails will be notified in addition to all admin users.
        </p>

        {/* Add New Email */}
        <div>
          <Label htmlFor="new-email" className="text-slate-300 mb-2 block">
            Add Email Address
          </Label>
          <div className="flex gap-2">
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="manager@example.com"
              className="flex-1"
            />
            <Button
              onClick={addEmail}
              className="bg-blue-600 hover:bg-blue-700 px-4"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Email List */}
        <div>
          <Label className="text-slate-300 mb-2 block">
            Current Notification Emails ({emails.length})
          </Label>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
              <span className="ml-2 text-slate-300">Loading emails...</span>
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No notification emails configured</p>
              <p className="text-sm">Only admin users will receive registration notifications</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {emails.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="text-white break-all">{email}</span>
                  </div>
                  <Button
                    onClick={() => removeEmail(email)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20 ml-2 flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={saveEmailSettings}
            disabled={isSaving}
            className="bg-green-600 hover:bg-green-700 min-w-[120px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-blue-300 font-medium mb-1">How it works</h4>
              <p className="text-slate-300 text-sm">
                When a new user registers on the site, notification emails will be sent to:
              </p>
              <ul className="text-slate-300 text-sm mt-2 space-y-1">
                <li>• All users with admin role</li>
                <li>• All email addresses configured above</li>
              </ul>
              <p className="text-slate-300 text-sm mt-2">
                The emails will include the new user's details and a link to approve/reject them.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};